import {
  Button,
  Form,
  Input,
  Row,
  Slider,
  Col,
  InputNumber,
  Select,
  Switch,
  notification,
  Checkbox,
} from "antd";
import { useState } from "react";
import Loading from "../../components/Loading";
import { ContentTitle } from "../../layout/ContentLayout";
import careCategoryOptions, {
  careCategorySelectOptions,
} from "../../utils/careCategoryOptions";
import Spacer from "../../components/Spacer";
import availabilityTimeOptions from "../../utils/availabilityTimeOptions";
import SubmitButton from "../../components/SubmitButton";
import { dayEnum } from "../../utils/dayUtils";
import { useUserMutation } from "../../api/Users";
import { ageGroupOptions } from "../../utils/ageGroupOptions";

const { useForm } = Form;

const CaregiverSettings = ({ user }) => {
  const [form] = useForm();
  const [submitting, setSubmitting] = useState();
  const [dirty, setDirty] = useState(false);
  const [selectedCaretype, setSelectedCaretype] = useState(user.CareType);

  const userMutation = useUserMutation(user?.UserId);
  const submit = () => {
    let values = form.getFieldsValue(null, (meta) => meta.touched);
    console.log(values);
    return;
    setSubmitting(true);
    userMutation
      .mutateAsync(values)
      .then((r) => {
        notification.open({
          message: "Success",
          description: "Your caregiver settings were updated",
        });
        setDirty(false);
      })
      .finally(() => setSubmitting(false));
  };

  const marks = {
    3: "3 years",
    19: "19+ years",
  };

  const formatter = (value) => `${value} years`;
  return !user ? (
    <Loading />
  ) : (
    <>
      <ContentTitle description="This is your public caregiver profile, viewable by parents in Carisity">
        Caregiver Settings
      </ContentTitle>
      <Form
        onFieldsChange={() => setDirty(true)}
        form={form}
        onFinish={submit}
        initialValues={user}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="CareType" label="Care Type">
              <Select
                options={careCategorySelectOptions(false)}
                onChange={(e) => setSelectedCaretype(e)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="Rate" label="Hourly Rate">
              <InputNumber addonBefore={"$"} addonAfter="/hr" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="Categories" label="Categories">
          <Select
            mode="tags"
            options={careCategoryOptions(false, selectedCaretype)}
            maxTagCount={5}
            allowClear
          />
        </Form.Item>
        <Form.Item
          name="Ages"
          label="Age Range"
          extra="The minimum and maximum ages of children you wish to provide care to"
        >
          <Checkbox.Group options={ageGroupOptions} />
          {/* <Slider
            min={3}
            max={19}
            // defaultValue={[3, 19]}
            range
            marks={marks}
            included={true}
            tooltip={{ formatter }}
          /> */}
        </Form.Item>

        <Form.Item name="Bio" label="Biography">
          <Input.TextArea maxLength={500} showCount rows={8} />
        </Form.Item>

        <Form.Item>
          <SubmitButton disabled={!dirty} submitting={submitting}>
            Update Settings
          </SubmitButton>
        </Form.Item>
      </Form>

      <Spacer />

      <ContentTitle description="The times you are available for scheduling">
        Availability
      </ContentTitle>

      <AvailabilityForm
        userId={user.UserId}
        availability={user?.Availability}
      />
    </>
  );
};

const AvailabilityForm = ({ userId, availability }) => {
  const [form] = useForm();
  const userUpdate = useUserMutation(userId);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);

  let initialValues = {
    Sunday: { Enabled: false, Day: 0, valid: true },
    Monday: { Enabled: false, Day: 1, valid: true },
    Tuesday: { Enabled: false, Day: 2, valid: true },
    Wednesday: { Enabled: false, Day: 3, valid: true },
    Thursday: { Enabled: false, Day: 4, valid: true },
    Friday: { Enabled: false, Day: 5, valid: true },
    Saturday: { Enabled: false, Day: 6, valid: true },
  };

  availability?.forEach((a) => {
    let dayOfWeek = dayEnum[a.Day].full;
    initialValues[dayOfWeek] = { ...a };
  });

  const submit = (values) => {
    setSubmitting(true);
    const availability = [];
    for (const obj in values) {
      availability.push({ ...values[obj] });
    }
    userUpdate
      .mutateAsync({ Availability: availability })
      .then(() => {
        setDirty(false);
        notification.open({
          message: "Success",
          description: "Your availability has been updated",
        });
      })
      .finally(() => setSubmitting(false));
  };

  const formRules = [
    {
      validator: (_, value) => {
        if (value.valid === false) {
          return Promise.reject();
        }

        return Promise.resolve();
      },
      message: "Your start time must be earlier than your end time",
    },
  ];

  return (
    <Form
      form={form}
      layout="horizontal"
      labelAlign="left"
      initialValues={initialValues}
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 20 }}
      onFinish={submit}
      onFieldsChange={() => setDirty(true)}
    >
      <Form.Item name="Sunday" label="Sunday" rules={formRules}>
        <DayAvailability />
      </Form.Item>
      <Form.Item name="Monday" label="Monday" rules={formRules}>
        <DayAvailability />
      </Form.Item>
      <Form.Item name="Tuesday" label="Tuesday" rules={formRules}>
        <DayAvailability />
      </Form.Item>
      <Form.Item name="Wednesday" label="Wednesday" rules={formRules}>
        <DayAvailability />
      </Form.Item>
      <Form.Item name="Thursday" label="Thursday" rules={formRules}>
        <DayAvailability />
      </Form.Item>
      <Form.Item name="Friday" label="Friday" rules={formRules}>
        <DayAvailability />
      </Form.Item>
      <Form.Item name="Saturday" label="Saturday" rules={formRules}>
        <DayAvailability />
      </Form.Item>
      <Form.Item>
        <SubmitButton disabled={!dirty} submitting={submitting}>
          Update Availability
        </SubmitButton>
      </Form.Item>
    </Form>
  );
};

const DayAvailability = ({ value, onChange }) => {
  const [Enabled, setEnabled] = useState(value?.Enabled || false);
  const [Start] = useState(value?.Start || "09:00");
  const [End] = useState(value?.End || "17:00");
  const [valid] = useState(value?.valid || true);
  const Day = value.Day;

  const triggerChange = (changedValue) => {
    onChange?.({
      Enabled,
      Day,
      valid,
      ...value,
      ...changedValue,
    });
  };

  const changeEnabled = (enabled) => {
    setEnabled(enabled);
    triggerChange({
      Enabled: enabled,
    });
  };

  const changeStart = (start) => {
    const valid = start < End;

    triggerChange({
      Start: start,
      valid: valid,
    });
  };

  const changeEnd = (end) => {
    const valid = end > Start;

    triggerChange({
      End: end,
      valid: valid,
    });
  };

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <div style={{ flex: "0 0 50px" }}>
        <Switch defaultChecked={Enabled} onChange={changeEnabled} />
      </div>
      <div style={{ flex: "1 1 auto", padding: "0 5px" }}>
        <Select
          disabled={!Enabled}
          defaultValue={Start}
          options={availabilityTimeOptions()}
          onChange={changeStart}
        />
      </div>
      <div style={{ flex: "1 1 auto", padding: "0 5px" }}>
        <Select
          disabled={!Enabled}
          defaultValue={End}
          options={availabilityTimeOptions()}
          onChange={changeEnd}
        />
      </div>
    </div>
  );
};

export default CaregiverSettings;
